import { useCallback, useEffect, useRef, useState } from 'react';
import { convertToGrayscale, getMediaConstraints, stopAllTracks } from '../utils/barcode-helper';

// Custom hook for scanning logic and camera state
const useBarcodeScanner = (args?: { onScanSuccess: (barcode: string) => void }) => {
  const [scanState, setScanState] = useState({
    isScanning: false,
    facingMode: 'environment',
    isTorchOn: false,
    showDialog: false,
    data: { typeName: '', scanData: '' },
  });
  const [tickParams, setTickParams] = useState({
    context: null,
    width: null,
    height: null,
    scanImageData: null,
  });
  const videoRef = useRef<any>(null);
  const canvasRef = useRef<any>(null);
  const audioRef = useRef<any>(null);
  const animationFrameId = useRef<any>(null);
  const scanImageDataRef = useRef<any>(null);
  const SCAN_INTERVAL = 150;
  const lastScanTimeRef = useRef<any>(0);
  const isScanningRef = useRef<any>(scanState.isScanning);

  // Sound feedback functions
  const playSuccessSound = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).Audio) {
      const audioContext = new (window as any).AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 800; // Higher pitch for success
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;

      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.00001, audioContext.currentTime + 0.1);
      oscillator.stop(audioContext.currentTime + 0.1);
    }
  }, []);

  const playErrorSound = useCallback(() => {
    if (typeof window !== 'undefined' && (window as any).Audio) {
      const audioContext = new (window as any).AudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = 200; // Lower pitch for error
      oscillator.type = 'sawtooth';
      gainNode.gain.value = 0.1;

      oscillator.start();
      gainNode.gain.exponentialRampToValueAtTime(0.00001, audioContext.currentTime + 0.2);
      oscillator.stop(audioContext.currentTime + 0.2);
    }
  }, []);

  useEffect(() => {
    isScanningRef.current = scanState.isScanning;
  }, [scanState.isScanning]);

  useEffect(() => {
    if (
      !!!scanState?.data?.scanData &&
      !!tickParams.context &&
      !!tickParams.width &&
      !!tickParams.height &&
      !!tickParams.scanImageData
    ) {
      animationFrameId.current = requestAnimationFrame(() =>
        tick(tickParams.context, tickParams.width, tickParams.height, tickParams.scanImageData),
      );
    }
  }, [scanState?.data?.scanData]);

  const handleError = (error: any) => {
    console.error('[CAMERA DEBUG] handleError called with:', error);
    console.error('[CAMERA DEBUG] Error name:', error?.name);
    console.error('[CAMERA DEBUG] Error message:', error?.message);
    console.error('[CAMERA DEBUG] Error stack:', error?.stack);

    // Specific iOS error handling
    if (error?.name === 'NotAllowedError') {
      console.error('[CAMERA DEBUG] Permission denied - user rejected camera access');
    } else if (error?.name === 'NotFoundError') {
      console.error('[CAMERA DEBUG] No camera found on device');
    } else if (error?.name === 'NotReadableError') {
      console.error('[CAMERA DEBUG] Camera already in use or hardware error');
    } else if (error?.name === 'OverconstrainedError') {
      console.error('[CAMERA DEBUG] Constraints not supported - error details:', error?.constraint);
    }

    setScanState((prev: any) => {
      return { ...prev, isScanning: false, data: null };
    });
  };
  const tick = async (context: any, width: any, height: any, scanImageData: any) => {
    if (!isScanningRef.current) {
      handleStopScan();
      return;
    }
    if (!!scanState?.data?.scanData) return;

    // Guard against null videoRef during unmount
    if (!videoRef.current?.srcObject) {
      return;
    }

    const now = Date.now();
    if (now - lastScanTimeRef.current < SCAN_INTERVAL) {
      animationFrameId.current = requestAnimationFrame(() => tick(context, width, height, scanImageData));
      return;
    }
    lastScanTimeRef.current = now;

    try {
      context.drawImage(videoRef.current, 0, 0, width, height);
      const imageData = context.getImageData(0, 0, width, height);
      const grayscaleImageData = convertToGrayscale(imageData);
      const results = await scanImageData(grayscaleImageData);
      if (results && results.length > 0 && !!!scanState.data?.scanData) {
        const decodedData = results[0]?.decode();

        setScanState(prev => ({
          ...prev,
          data: {
            typeName: results[0]?.typeName.replace('ZBAR_', ''),
            scanData: decodedData,
          },
        }));

        args?.onScanSuccess(decodedData);
        window?.navigator?.vibrate?.(300);

        // Clear data after callback to allow subsequent scans
        setTimeout(() => {
          setScanState(prev => ({
            ...prev,
            data: { typeName: '', scanData: '' },
          }));
        }, 1000);
      } else {
        animationFrameId.current = requestAnimationFrame(() => tick(context, width, height, scanImageData));
      }
    } catch (error) {
      console.error('[SCAN DEBUG] Error in tick loop:', error);
      // Stop the loop on error to prevent infinite error loop
      return;
    }
  };

  const handleScan = async () => {

    setScanState((prev: any) => {
      return { ...prev, data: null, isScanning: true };
    });

    try {
      // Check if running on HTTPS (required for iOS)
      if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
        console.error('[CAMERA DEBUG] NOT on HTTPS - camera access will fail on iOS');
        throw new Error('Camera access requires HTTPS on mobile devices');
      }

      if (!scanImageDataRef.current) {
        const zbar = await import('@undecaf/zbar-wasm');
        scanImageDataRef.current = zbar.scanImageData;
      }

      const scanImageData = scanImageDataRef.current;

      const mediaConstraints = await getMediaConstraints(scanState.facingMode);

      const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);

      videoRef.current.srcObject = stream;

      videoRef.current.onplay = async () => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        const width = videoRef.current.videoWidth;
        const height = videoRef.current.videoHeight;
        canvas.width = width;
        canvas.height = height;


        setTickParams({ context, width, height, scanImageData });
        animationFrameId.current = requestAnimationFrame(() => tick(context, width, height, scanImageData));
      };

      // Add timeout to detect if onplay never fires
      setTimeout(() => {
        if (videoRef.current && !videoRef.current.videoWidth) {
          console.error('[CAMERA DEBUG] TIMEOUT: video.onplay never fired after 5 seconds');
          console.error('[CAMERA DEBUG] Video element state:', {
            srcObject: !!videoRef.current.srcObject,
            readyState: videoRef.current.readyState,
            videoWidth: videoRef.current.videoWidth,
            videoHeight: videoRef.current.videoHeight,
          });
        }
      }, 5000);

    } catch (error) {
      console.error('[CAMERA DEBUG] ERROR in handleScan:', error);
      const err = error as Error;
      console.error('[CAMERA DEBUG] Error name:', err?.name);
      console.error('[CAMERA DEBUG] Error message:', err?.message);
      console.error('[CAMERA DEBUG] Error stack:', err?.stack);
      handleError(err);
    }
  };

  const handleStopScan = useCallback(() => {
    setScanState(prev => {
      return { ...prev, isScanning: false };
    });

    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
      animationFrameId.current = null;
    }

    if (videoRef.current?.srcObject) {
      stopAllTracks(videoRef.current.srcObject);
      videoRef.current.srcObject = null;
    }

    setTickParams({ context: null, width: null, height: null, scanImageData: null });
  }, []);

  const handleSwitchCamera = async () => {
    if (!videoRef.current) {
      console.error('[CAMERA DEBUG] videoRef not available');
      return;
    }

    if (videoRef.current.srcObject) {
      stopAllTracks(videoRef.current.srcObject);
    }

    const newFacingMode = scanState.facingMode === 'user' ? 'environment' : 'user';

    try {
      const mediaConstraints = await getMediaConstraints(newFacingMode);

      const stream = await navigator.mediaDevices.getUserMedia(mediaConstraints);

      videoRef.current.srcObject = stream;
      setScanState(prev => ({ ...prev, facingMode: newFacingMode }));
    } catch (error) {
      console.error('[CAMERA DEBUG] Error switching camera:', error);
      const err = error as Error;
      console.error('[CAMERA DEBUG] Error name:', err?.name);
      console.error('[CAMERA DEBUG] Error message:', err?.message);
    }
  };

  const handleToggleTorch = async () => {
    if (!videoRef.current) return;
    const tracks = videoRef.current.srcObject?.getVideoTracks();
    if (tracks && tracks.length > 0) {
      const track = tracks[0];
      const capabilities = track.getCapabilities();
      if (!capabilities.torch) return;
      try {
        await track.applyConstraints({
          advanced: [
            {
              fillLightMode: scanState.isTorchOn ? 'flash' : 'off',
              torch: !scanState.isTorchOn,
            },
          ],
        });
        setScanState(prev => ({ ...prev, isTorchOn: !prev.isTorchOn }));
      } catch (error) {
        console.error('Error toggling flash:', error);
      }
    }
  };

  const handleDataCopy = () => {
    if (!scanState.data) return;
    navigator.clipboard.writeText(scanState.data.scanData || '').then(
      () => ({}),
      () => ({}),
    );
    setScanState(prev => ({ ...prev, showDialog: false }));
  };

  const handleShowDialog = () => setScanState(prev => ({ ...prev, showDialog: !prev.showDialog }));

  const handleClearData = () => {
    setScanState(prev => ({ ...prev, data: { typeName: '', scanData: '' } }));
  };

  useEffect(() => {
    return () => {
      // Cancel any pending animation frame
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }

      // Stop video tracks to release camera
      if (videoRef.current?.srcObject) {
        stopAllTracks(videoRef.current.srcObject);
        videoRef.current.srcObject = null;
      }

    };
  }, []);

  return {
    scanState,
    videoRef,
    canvasRef,
    audioRef,
    handleScan,
    handleStopScan,
    handleSwitchCamera,
    handleToggleTorch,
    handleDataCopy,
    handleShowDialog,
    handleClearData,
    playSuccessSound,
    playErrorSound,
  };
};

export default useBarcodeScanner;
